import FindBottleModal from "../components/FindBottleModal";
import { useEffect, useMemo, useRef, useState, type CSSProperties } from "react";
import { useNavigate } from "react-router-dom";
import SearchBar from "../components/SearchBar";
import FilterPills from "../components/FilterPills";
import DrinkList from "../components/DrinkList";
import RecipeDetail from "../components/RecipeDetail";
import StickyHeader, { SyncIndicator } from "../components/StickyHeader";
import LoadedTray from "../components/LoadedTray";
import KioskControls from "../components/common/KioskControls";
import TableSetupAlertBanner from "../components/common/TableSetupAlertBanner";
import { ApiError, listDrinks, listPrepRecipes, searchDrinks, getDrink, logActivity, pingPresence, type PrepListItem, type PresenceItem } from "../lib/api";
import { logout, whoami } from "../lib/auth";
import { DEFAULT_COCKTAIL_COUNT, useSessionStore } from "../state/sessionStore";
import { supabase } from "../lib/supabase";
import type { DrinkDetail, DrinkListItem } from "../types/drink";
import type { SessionUser as AuthUser } from "../types/auth";

const SEARCH_DEBOUNCE_MS = 200;

export default function Station() {
  const navigate = useNavigate();
  const session = useSessionStore();
  const { state } = session;

  const [user, setUser] = useState<AuthUser | null>(null);
  const [query, setQuery] = useState("");
  const [findingBottle, setFindingBottle] = useState(false);
  const [drinks, setDrinks] = useState<DrinkListItem[]>([]);
  const [loadingList, setLoadingList] = useState(true);
  const [listError, setListError] = useState<string | null>(null);
  const [lastSyncedAt, setLastSyncedAt] = useState<number | null>(null);
  const [detail, setDetail] = useState<DrinkDetail | null>(null);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [detailError, setDetailError] = useState<string | null>(null);
  // Realtime subscription refresh counter trigger
  const [refreshCount, setRefreshCount] = useState(0);
  // Phase 10 polish: cache the active prep recipes once at mount so we can
  // render summary info inline on any drink that references them.
  const [preps, setPreps] = useState<PrepListItem[]>([]);
  // Presence list for other active tablets in the same room
  const [presenceList, setPresenceList] = useState<PresenceItem[]>([]);

  /**
   * Ephemeral UI flag: if true, render the recipe list even when there's an
   * active drink. Set when the user taps Back, switches filters, or starts
   * typing in search. Cleared when they pick a tray slot or a list item.
   * NOT persisted — the active drink stays in state.activeSlug so the tray
   * + detail come right back on the next tray-slot tap.
   */
  const [showListOverride, setShowListOverride] = useState(false);

  // Name cache so the tray can render names for slugs we haven't fully fetched yet
  const [nameCache, setNameCache] = useState<Record<string, string>>({});
  const cacheNames = (items: { slug: string; name: string }[]) => {
    setNameCache((prev) => {
      let changed = false;
      const next = { ...prev };
      for (const it of items) {
        if (next[it.slug] !== it.name) {
          next[it.slug] = it.name;
          changed = true;
        }
      }
      return changed ? next : prev;
    });
  };

  const debounceRef = useRef<number | null>(null);

  // Identify user + cache active preps for inline rendering on the recipe detail.
  useEffect(() => {
    void whoami().then((u) => setUser(u));
    void listPrepRecipes("active_only")
      .then((r) => setPreps(r.preps))
      .catch(() => undefined);
  }, []);

  // Periodically report this tablet's current making state and get other tablets in the same room.
  useEffect(() => {
    if (!user) return;

    const doPing = () => {
      const activeDrinkSlug = state.activeSlug || null;
      const activeDrinkName = activeDrinkSlug
        ? (nameCache[activeDrinkSlug] || detail?.drink?.name || activeDrinkSlug)
        : null;
      const activeDrinkCount = activeDrinkSlug
        ? (state.cocktailCounts[activeDrinkSlug] ?? DEFAULT_COCKTAIL_COUNT)
        : null;

      void pingPresence(activeDrinkSlug, activeDrinkName, activeDrinkCount)
        .then((resp) => {
          if (resp && resp.ok && Array.isArray(resp.presence)) {
            setPresenceList(resp.presence);
          }
        })
        .catch((err) => {
          console.warn("Failed to ping presence:", err);
        });
    };

    doPing();
    const interval = window.setInterval(doPing, 10000);
    return () => window.clearInterval(interval);
  }, [user, state.activeSlug, state.cocktailCounts, nameCache, detail]);

  // Fetch list (debounced) on filter / query changes / realtime triggers.
  useEffect(() => {
    let cancelled = false;
    async function refetch(showSpinner: boolean) {
      if (showSpinner) setLoadingList(true);
      setListError(null);
      try {
        const trimmed = query.trim();
        const resp = trimmed
          ? await searchDrinks(trimmed)
          : await listDrinks(state.filter);
        if (cancelled) return;
        setDrinks(resp.drinks);
        cacheNames(resp.drinks);
        setLastSyncedAt(Date.now());
      } catch (err: any) {
        if (cancelled) return;
        // Only wipe the list and logout on user-initiated fetches (showSpinner).
        // Silent background polls keep stale data visible so a transient network
        // blip or a 401 during an edge-function redeploy doesn't blank the menu
        // or permanently de-provision a 365-day tablet token.
        if (showSpinner) {
          setDrinks([]);
          setListError(err?.message ?? "Could not load drinks");
          if (err instanceof ApiError && err.status === 401) {
            logout();
            navigate("/login", { replace: true });
          }
        } else {
          // Silent poll failed — keep stale drinks, just surface the sync gap.
          setListError(err?.message ?? "Could not sync drinks");
        }
      } finally {
        if (!cancelled && showSpinner) setLoadingList(false);
      }
    }
    if (debounceRef.current) window.clearTimeout(debounceRef.current);
    debounceRef.current = window.setTimeout(() => refetch(true), SEARCH_DEBOUNCE_MS);

    return () => {
      cancelled = true;
      if (debounceRef.current) window.clearTimeout(debounceRef.current);
    };
  }, [state.filter, query, navigate, refreshCount]);

  // Setup Supabase Realtime Postgres Change subscriptions for zero-lag menu updates.
  // Listening to INSERT/UPDATE/DELETE events on drinks and ingredients tables.
  useEffect(() => {
    const channel = supabase
      .channel("bar_manual_realtime")
      .on(
        "postgres_changes",
        { event: "*", schema: "bar_manual", table: "drinks" },
        () => {
          // Trigger a silent, non-spinner list refresh
          setRefreshCount((prev) => prev + 1);
        }
      )
      .on(
        "postgres_changes",
        { event: "*", schema: "bar_manual", table: "ingredients" },
        () => {
          // Trigger a silent detail refresh and a list refresh
          setRefreshCount((prev) => prev + 1);
          reloadActiveDetail();
        }
      )
      .subscribe();

    return () => {
      void supabase.removeChannel(channel);
    };
  }, []);

  // Fetch detail when activeSlug changes
  useEffect(() => {
    if (!state.activeSlug) {
      setDetail(null);
      setDetailError(null);
      return;
    }
    setLoadingDetail(true);
    setDetailError(null);
    setDetail(null);
    const slugToFetch = state.activeSlug;
    void getDrink(slugToFetch)
      .then((d) => {
        setDetail(d);
        cacheNames([{ slug: d.drink.slug, name: d.drink.name }]);
        // Phase 4: best-effort activity log. Drives Today + Popular filters.
        void logActivity(slugToFetch, "load");
      })
      .catch((err: any) => setDetailError(err?.message ?? "Could not load recipe"))
      .finally(() => setLoadingDetail(false));
  }, [state.activeSlug]);

  // Re-fetch the active drink (no spinner) — used when the bartender flags
  // an ingredient out, so the row repaints with the unavailable flag.
  function reloadActiveDetail() {
    if (!state.activeSlug) return;
    void getDrink(state.activeSlug)
      .then((d) => setDetail(d))
      .catch(() => undefined);
  }

  // Loaded tray inputs
  const trayDrinks = useMemo(
    () =>
      state.loadedSlugs.map((slug) => ({
        slug,
        name: nameCache[slug] ?? slug,
        cocktailCount: state.cocktailCounts[slug] ?? DEFAULT_COCKTAIL_COUNT,
      })),
    [state.loadedSlugs, state.cocktailCounts, nameCache]
  );

  const emptyMessage = useMemo(() => {
    if (listError) return listError;
    if (query.trim()) return `No drinks match "${query.trim()}".`;
    if (state.filter === "popular") return "No popular drinks logged in the last 30 days.";
    if (state.filter === "today") return "No drinks featured or loaded today yet.";
    if (state.filter === "cocktails") return "No cocktails on the menu right now.";
    if (state.filter === "old_fashioned") return "No Old Fashioned variants on the menu right now.";
    return "No drinks found.";
  }, [state.filter, query, listError]);

  function onLogout() {
    logout();
    navigate("/login", { replace: true });
  }

  function onTapHome() {
    // Reset to defaults — clear search, filter back to today, end round.
    setQuery("");
    session.endRound();
    setShowListOverride(false);
  }

  function onSelectFromList(slug: string) {
    session.selectFromList(slug);
    setShowListOverride(false);
    window.scrollTo({ top: 0, behavior: "smooth" });
  }

  function onSelectFromTray(slug: string) {
    session.selectFromTray(slug);
    setShowListOverride(false);
  }

  function onLoadMore() {
    setShowListOverride(true);
    window.scrollTo({ top: 0, behavior: "smooth" });
  }

  const activeDetail =
    state.activeSlug && detail && detail.drink.slug === state.activeSlug ? detail : null;
  const activeCount = state.activeSlug
    ? state.cocktailCounts[state.activeSlug] ?? DEFAULT_COCKTAIL_COUNT
    : 1;
  const activeChecked = state.activeSlug
    ? state.checkedIngredients[state.activeSlug] ?? {}
    : {};
  const activeFinishChecked = state.activeSlug
    ? state.checkedFinishSteps[state.activeSlug] ?? {}
    : {};

  const showList = showListOverride || !state.activeSlug || (!activeDetail && !loadingDetail && !detailError);

  return (
    <main style={styles.shell}>
      {findingBottle && <FindBottleModal onClose={() => setFindingBottle(false)} />}
      <TableSetupAlertBanner />
      <div style={styles.column}>
        <StickyHeader
          onHome={onTapHome}
          searchSlot={
            <SearchBar
              value={query}
              onChange={(v) => {
                setQuery(v);
                setShowListOverride(true);
              }}
              onClear={() => {
                setQuery("");
              }}
            />
          }
          right={
            <div style={{ display: "flex", gap: 6, flexDirection: "column" }}>
              <button type="button" onClick={() => setFindingBottle(true)} style={{ ...styles.adminLink, minHeight: 44 }} aria-haspopup="dialog">Find bottle</button>
              <div style={{ display: "flex", gap: 6 }}>
              {user?.bar_roles?.some(
                (r) => r === "bar_manager" || r === "bar_owner"
              ) ? (
                <>
                  <button
                    type="button"
                    onClick={() => navigate("/")}
                    style={styles.adminLink}
                  >
                    OS Hub
                  </button>
                  <button
                    type="button"
                    onClick={() => navigate("/admin")}
                    style={styles.adminLink}
                  >
                    Admin
                  </button>
                </>
              ) : (
                <button
                  type="button"
                  onClick={() => navigate("/")}
                  style={styles.adminLink}
                >
                  Hub
                </button>
              )}
              </div>
            </div>
          }
        />

        <div style={styles.eyebrowRow}>
          <p style={styles.eyebrow}>Station · {user?.display_label ?? "…"}</p>
          <SyncIndicator lastSyncedAt={lastSyncedAt} />
          <div style={{ marginLeft: "auto", display: "flex", gap: 8, alignItems: "center" }}>
            <KioskControls compact />
            {state.loadedSlugs.length > 0 && (
              <button
                type="button"
                onClick={() => {
                  session.endRound();
                  setShowListOverride(false);
                }}
                style={styles.endRoundBtn}
              >
                End round
              </button>
            )}
          </div>
        </div>

        <FilterPills
          active={state.filter}
          onChange={(f) => {
            session.setFilter(f);
            setQuery("");
            setShowListOverride(true);
          }}
        />

        {state.loadedSlugs.length > 0 && (
          <LoadedTray
            drinks={trayDrinks}
            activeSlug={state.activeSlug}
            onSelect={onSelectFromTray}
            onRemove={(slug) => session.removeLoaded(slug)}
            onLoadMore={onLoadMore}
          />
        )}

        {/* Real-time tablet presence in the same room */}
        {presenceList.length > 0 && (
          <div style={styles.presenceRow} aria-live="polite">
            {presenceList.map((p) => (
              <div key={p.customer_id} style={styles.presenceChip}>
                <span style={{ fontSize: 13 }}>👥</span>
                <span>
                  <strong>{p.label}</strong>{" "}
                  {p.active_drink_slug ? (
                    <>
                      is currently making{" "}
                      <span style={{ color: "var(--gold)", fontWeight: 800 }}>
                        {p.active_drink_name}
                      </span>
                      {p.active_drink_count && p.active_drink_count > 1 ? (
                        <strong style={{ color: "var(--gold)" }}>
                          {" "}× {p.active_drink_count}
                        </strong>
                      ) : (
                        ""
                      )}
                    </>
                  ) : (
                    <span style={{ color: "var(--muted)" }}>is currently idle</span>
                  )}
                </span>
              </div>
            ))}
          </div>
        )}

        {showList && (
          <section style={styles.section}>
            <p style={styles.countLine}>
              {loadingList
                ? "Loading…"
                : `${drinks.length} ${drinks.length === 1 ? "drink" : "drinks"}`}
              {query.trim() && ` matching "${query.trim()}"`}
            </p>
            <DrinkList
              drinks={drinks}
              loading={loadingList}
              onSelect={onSelectFromList}
              emptyMessage={emptyMessage}
            />
          </section>
        )}

        {!showList && state.activeSlug && loadingDetail && (
          <p style={{ color: "var(--muted)", marginTop: 24 }}>Loading recipe…</p>
        )}

        {!showList && state.activeSlug && detailError && (
          <div style={styles.detailError}>
            <p>{detailError}</p>
            <button
              type="button"
              onClick={() => setShowListOverride(true)}
              style={styles.signOut}
            >
              ← Back to list
            </button>
          </div>
        )}

        {!showList && activeDetail && (
          <RecipeDetail
            detail={activeDetail}
            cocktailCount={activeCount}
            checkedIngredients={activeChecked}
            checkedFinishSteps={activeFinishChecked}
            onSetCount={(n) => session.setCocktailCount(state.activeSlug!, n)}
            onToggleIngredient={(ingId) =>
              session.toggleIngredient(state.activeSlug!, ingId)
            }
            onToggleFinishStep={(stepId) =>
              session.toggleFinishStep(state.activeSlug!, stepId)
            }
            onResetCocktail={() => session.resetCocktail(state.activeSlug!)}
            onClearChecks={() => session.clearChecks(state.activeSlug!)}
            onBack={() => setShowListOverride(true)}
            reporterName={user?.display_label ?? null}
            preps={preps}
            onIngredientFlagged={reloadActiveDetail}
            onCompleteDrink={() => {
              // Drink complete: remove from the loaded tray AND drop back
              // to the list. removeLoaded handles the gc — wipes
              // cocktailCount + checkedIngredients + checkedFinishSteps
              // for that slug — and clears activeSlug if it pointed at
              // the just-removed drink.
              if (state.activeSlug) session.removeLoaded(state.activeSlug);
              setShowListOverride(true);
            }}
          />
        )}

        {/* Footer — sign-out lives here on purpose. Bartenders rarely need
            it, and putting it in the header risks accidental taps that drop
            a tablet's session mid-service. */}
        <footer style={styles.footer}>
          <button type="button" onClick={onLogout} style={styles.footerSignOut}>
            Sign out
          </button>
        </footer>
      </div>
    </main>
  );
}

const styles: Record<string, CSSProperties> = {
  shell: { padding: 12 },
  column: { maxWidth: 540, margin: "0 auto" },
  eyebrowRow: {
    display: "flex",
    alignItems: "center",
    gap: 12,
    flexWrap: "wrap",
    marginTop: 6,
    marginBottom: 6,
  },
  eyebrow: {
    margin: 0,
    fontSize: 11,
    fontWeight: 800,
    letterSpacing: 0.6,
    textTransform: "uppercase",
    color: "var(--gold)",
  },
  footer: {
    marginTop: 48,
    paddingTop: 24,
    paddingBottom: 24,
    borderTop: "1px solid var(--border)",
    textAlign: "center",
  },
  footerSignOut: {
    // WCAG 1.4.3 — drop opacity:0.5; muted color on bg already passes 8:1.
    background: "transparent",
    color: "var(--muted)",
    border: 0,
    fontSize: 11,
    fontWeight: 600,
    cursor: "pointer",
    padding: "8px 16px",
    letterSpacing: 0.4,
    textTransform: "uppercase",
    textDecoration: "underline",
    textUnderlineOffset: 3,
  },
  adminLink: {
    background: "rgba(249,175,54,0.12)",
    color: "var(--gold)",
    border: "1px solid rgba(249,175,54,0.3)",
    borderRadius: 10,
    padding: "4px 10px",
    fontSize: 11,
    fontWeight: 800,
    textDecoration: "none",
    letterSpacing: 0.4,
    textTransform: "uppercase",
  },
  endRoundBtn: {
    background: "transparent",
    color: "#FF8A7A",
    border: "1px solid rgba(224,80,64,0.4)",
    borderRadius: 10,
    padding: "4px 10px",
    fontSize: 11,
    fontWeight: 800,
    letterSpacing: 0.4,
    textTransform: "uppercase",
    cursor: "pointer",
  },
  section: { marginTop: 10 },
  countLine: {
    margin: "0 0 8px",
    fontSize: 11,
    color: "var(--muted)",
    fontWeight: 700,
    letterSpacing: 0.4,
    textTransform: "uppercase",
  },
  detailError: {
    background: "var(--surface)",
    border: "1px solid rgba(224,80,64,0.4)",
    color: "#FF8A7A",
    borderRadius: 12,
    padding: 16,
    marginTop: 16,
  },
  presenceRow: {
    display: "flex",
    flexDirection: "column",
    gap: 6,
    background: "rgba(249,175,54,0.06)",
    border: "1px dashed rgba(249,175,54,0.3)",
    borderRadius: 14,
    padding: 10,
    marginTop: 8,
  },
  presenceChip: {
    fontSize: 11,
    fontWeight: 600,
    color: "var(--text)",
    display: "flex",
    alignItems: "center",
    gap: 6,
  },
};
